import { colors as C } from '@/design-system'

export const BREEZE_MANAGED_METHODS = ['email', 'sms', 'manual', 'custom', 'call']

export const isBreezeManaged = method => BREEZE_MANAGED_METHODS.includes(method)

// Capacity-specific block palettes, derived from the shared design tokens.
export const BLOCK_COLORS = [
  {
    bg: `${C.orange}20`, border: C.orange, text: C.orange, handle: `${C.orange}60`,
  },
  {
    bg: `${C.lavender}35`, border: C.wineMid, text: C.wineMid, handle: `${C.wineMid}60`,
  },
  {
    bg: `${C.pink}45`, border: '#c44', text: '#a33', handle: '#c4444460',
  },
]

export const SLOTS15 = (() => {
  const out = []
  for (let h = 8; h < 23; h += 1) {
    [0, 15, 30, 45].forEach(m => out.push(`${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`))
  }
  return out
})()

export const DAYS_DATA = [
  {
    key: 'mon', label: 'Monday', short: 'Mon', dayName: 'Monday', wk: false,
  },
  {
    key: 'tue', label: 'Tuesday', short: 'Tue', dayName: 'Tuesday', wk: false,
  },
  {
    key: 'wed', label: 'Wednesday', short: 'Wed', dayName: 'Wednesday', wk: false,
  },
  {
    key: 'thu', label: 'Thursday', short: 'Thu', dayName: 'Thursday', wk: false,
  },
  {
    key: 'fri', label: 'Friday', short: 'Fri', dayName: 'Friday', wk: false,
  },
  {
    key: 'sat', label: 'Saturday', short: 'Sat', dayName: 'Saturday', wk: true,
  },
  {
    key: 'sun', label: 'Sunday', short: 'Sun', dayName: 'Sunday', wk: true,
  },
]

export const slotIdx = s => {
  const [h, m] = s.split(':').map(Number)
  return (h - 8) * 4 + m / 15
}

export const idxSlot = i => {
  const h = Math.floor(i / 4) + 8
  const m = (i % 4) * 15
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`
}

export const addMin = (slot, min) => {
  const [h, m] = slot.split(':').map(Number)
  const t = h * 60 + m + min
  return `${String(Math.floor(t / 60)).padStart(2, '0')}:${String(t % 60).padStart(2, '0')}`
}

export const initDays = () => {
  const d = {}
  DAYS_DATA.forEach(day => {
    d[day.key] = { enabled: true, blocks: [recommendedBlock()] }
  })
  return d
}

export const newBlockId = () => Math.random().toString(36).slice(2, 8)

export const DEFAULT_MAX = 15
// LocationTimeSlot.weekday is 0=Sunday … 6=Saturday.
const WEEKDAY_KEYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat']
const GRID_START_MIN = 8 * 60

// The single source of Breeze's recommended block (11:00–22:30, default cap). Reused by the
// per-day fallback, the editor's "Add interval"/"Use recommended", and initDays.
export const recommendedBlock = () => ({
  id: newBlockId(), slotId: null, from: slotIdx('11:00'), to: slotIdx('22:30'), max: DEFAULT_MAX, busy: false,
})

// The grid is 15-min slots from 08:00 (idx 0). openFrom/openUntil are "HH:MM" clock strings
// on the wire; we tolerate integer minutes too in case a payload still sends them.
const minToIdx = min => Math.min(SLOTS15.length, Math.max(0, Math.round((min - GRID_START_MIN) / 15)))
const idxToMin = i => i * 15 + GRID_START_MIN
const toMinutes = v => {
  if (typeof v === 'number') return v
  const [h, m] = String(v).split(':').map(Number)
  return (h * 60) + (m || 0)
}
const minToClock = min => `${String(Math.floor(min / 60)).padStart(2, '0')}:${String(min % 60).padStart(2, '0')}`

// `id` is the editor's internal handle; `slotId` is the server LocationTimeSlot UUID we must
// echo back on save so the reconcile updates (not recreates) the row. New blocks have no slotId.
// openUntil is the seating cutoff; the bar shows the FULL block, so its end = openUntil + duration.
export const slotToBlock = (slot, duration = 0) => ({
  id: newBlockId(),
  slotId: slot.id || null,
  from: minToIdx(toMinutes(slot.openFrom)),
  to: minToIdx(toMinutes(slot.openUntil) + duration),
  max: slot.maxDateCount > 0 ? slot.maxDateCount : DEFAULT_MAX,
  busy: !!slot.busy,
})

// Inverse of daysFromSlots: the COMPLETE desired slot set for the PUT reconcile. Only enabled
// days contribute; omitted days/blocks are deleted server-side. Existing blocks keep their
// slotId so the server updates them; new blocks send id: null to be created.
// The bar end is the full block (openUntil + duration); subtract the duration back out to
// store the seating cutoff. This is symmetric with slotToBlock, so an unchanged round-trip
// preserves openUntil, and changing only the duration shifts the cutoff without moving the bar.
export const daysToTimeSlots = (days, duration = 0) => {
  const out = []
  DAYS_DATA.forEach(day => {
    const d = days[day.key]
    if (!d || !d.enabled) return
    const weekday = WEEKDAY_KEYS.indexOf(day.key)
    d.blocks.forEach(b => {
      // The seating window must be non-empty: the backend requires openUntil > openFrom (it
      // reads openUntil <= openFrom as an overnight slot). A block whose length <= duration
      // has no window; it's blocked at the save layer (DateCapacity.hasTooShortBlock), and
      // skipping it here is a defensive backstop so an invalid slot can never be sent.
      const openFromMin = idxToMin(b.from)
      const openUntilMin = idxToMin(b.to) - duration
      if (openUntilMin <= openFromMin) return
      out.push({
        id: b.slotId || null,
        weekday,
        openFrom: minToClock(openFromMin),
        openUntil: minToClock(openUntilMin),
        maxDateCount: b.max,
        busy: !!b.busy,
      })
    })
  })
  return out
}

// Per-day editor state from a location's LocationTimeSlots. Days that have slots are shown
// enabled with those slots. A day with no slot is left off but pre-seeded with the
// recommended default block, so checking that day reveals the default. There is no first-run
// seeding: a venue with no slots at all starts every day unchecked, and the partner opts each
// day in explicitly.
export const daysFromSlots = (slots, duration = 0) => {
  const byDay = {}
  const list = slots || []
  list.forEach(s => {
    const key = WEEKDAY_KEYS[s.weekday]
    const block = slotToBlock(s, duration)
    if (key && block.to > block.from) {
      byDay[key] = byDay[key] || []
      byDay[key].push(block)
    }
  })
  const d = {}
  DAYS_DATA.forEach(day => {
    const blocks = byDay[day.key]
    d[day.key] = blocks && blocks.length
      ? { enabled: true, blocks: blocks.sort((a, b) => a.from - b.from) }
      : { enabled: false, blocks: [recommendedBlock()] }
  })
  return d
}
